import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { apiClient } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Reusable user picker.
 *
 * Every form that needs to choose a user (Impersonate target, future
 * role-assign-by-user flows, …) uses THIS component instead of asking
 * the admin to paste a UUID. Shows email + display name + primary
 * partner path so the choice is readable.
 *
 * The render is a filterable native <datalist> combo: a text input
 * bound to an id-or-email string, with a datalist of email → id. On
 * blur, if the typed value exactly matches one of the options, the
 * parent's `onChange(id)` fires with the matched user's id; otherwise
 * `onChange("")`. This keeps the keyboard UX (type to filter) while
 * avoiding a heavier combobox dependency.
 */
export interface UserPickerProps {
  value: string;
  onChange: (id: string) => void;
  /** Optional client-side predicate (e.g. exclude disabled users). */
  filter?: (u: { id: string; email: string; is_disabled: boolean }) => boolean;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
}

export function UserPicker(props: UserPickerProps): JSX.Element {
  const query = useQuery({
    queryKey: ["users", "picker"],
    queryFn: () => apiClient.listUsers(),
    retry: false,
  });

  const users = useMemo(() => {
    const all = query.data?.users ?? [];
    const filtered = props.filter ? all.filter(props.filter) : all;
    return filtered.slice().sort((a, b) => a.email.localeCompare(b.email));
  }, [query.data, props.filter]);

  // The <input> binds to the user's EMAIL (the lookup key admins
  // recognise). We keep the backing id in sync via the datalist match.
  // Initial value - find the email matching the incoming id, or empty.
  const initialEmail = users.find((u) => u.id === props.value)?.email ?? "";
  const [text, setText] = useState(initialEmail);

  // Re-sync when the parent's value changes from the outside
  // (e.g. a deep-link sets ?target=<id>).
  useMemo(() => {
    const match = users.find((u) => u.id === props.value);
    if (match && match.email !== text) setText(match.email);
  }, [props.value, users, text]);

  const listId = props.id ? `${props.id}-options` : "user-picker-options";

  const commit = (raw: string): void => {
    const match = users.find((u) => u.email === raw);
    props.onChange(match ? match.id : "");
  };

  return (
    <>
      <input
        id={props.id}
        name={props.name}
        type="text"
        list={listId}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          commit(e.target.value);
        }}
        placeholder={
          props.placeholder ?? (query.isLoading ? "Loading users…" : "Type an email to pick a user")
        }
        required={props.required}
        disabled={props.disabled || query.isLoading}
        autoComplete="off"
        spellCheck={false}
        className={cn(
          "w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60",
          props.className,
        )}
      />
      <datalist id={listId}>
        {users.map((u) => {
          const display =
            [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email.split("@")[0];
          return (
            <option key={u.id} value={u.email}>
              {display}
              {u.primary_partner?.path ? ` · ${u.primary_partner.path}` : ""}
              {u.is_disabled ? " (disabled)" : ""}
            </option>
          );
        })}
      </datalist>
    </>
  );
}
